import store from '../../../../state';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { aggregateDailyForecast, aggregate3HourIntervals } from './DailyForecastListWidget';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export class DailyForecastStripWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Daily Glance Outlook';
    const optPeriod = widget.options?.period || 'auto';
    const requestedHorizon = optPeriod !== 'auto' ? optPeriod : ctx.viewDays;
    const days = aggregateDailyForecast(ctx, requestedHorizon);

    const activeSourceKey = (widget.options?.dataSource && widget.options.dataSource !== 'auto')
      ? widget.options.dataSource
      : (ctx.activeTabId && ctx.activeTabId !== 'blend' ? ctx.activeTabId : 'auto');

    const lat = ctx.lat;
    const lon = ctx.lon;
    const isCA = lat >= 41.6 && lat <= 83.1 && lon >= -141.0 && lon <= -52.6 && (lat >= 49.0 || (lat >= 42.0 && lon < -79.0));
    const isUS = (lat >= 24.5 && lat <= 49.38 && lon >= -124.8 && lon <= -66.9) ||
                 (lat >= 51.0 && lat <= 71.5 && lon >= -179.0 && lon <= -129.0) ||
                 (lat >= 18.9 && lat <= 22.3 && lon >= -160.3 && lon <= -154.8);
    const autoAgency = isUS ? 'NWS' : (isCA ? 'ECCC' : 'ECMWF');

    const isImperial = store.getState().units === 'imperial';
    const speedUnit = isImperial ? 'mph' : 'km/h';

    const activeH = String(requestedHorizon).toLowerCase();
    const is3 = activeH.startsWith('3');
    const is5 = activeH.startsWith('5');
    const is7 = activeH.startsWith('7');
    const isMax = activeH === 'max' || activeH === '10';

    const enableHourly = widget.options?.enableHourlyExpansion !== false;

    const tilesHtml = days.map((d, dIdx) => {
      const rainHtml = d.rainPct > 0
        ? `<span style="font-size: 0.65rem; font-weight: 700; color: #00d4ff; font-family: monospace;">💧${d.rainPct}%</span>`
        : `<span style="font-size: 0.65rem; color: #64748b; opacity: 0.3;">--</span>`;

      return `
        <div class="daily-strip-tile" data-widget-id="${widget.id}" data-day-idx="${dIdx}" style="flex: 0 0 76px; background: rgba(15, 23, 42, 0.65); border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 10px; padding: 8px 6px; display: flex; flex-direction: column; align-items: center; gap: 4px; text-align: center; cursor: ${enableHourly ? 'pointer' : 'default'}; transition: all 0.15s ease;" title="${enableHourly ? 'Click to toggle 3-hour forecast glance' : ''}">
          <div style="font-size: 0.75rem; font-weight: 700; color: #f8fafc;">${d.dayLabel}</div>
          <div style="font-size: 0.62rem; color: #94a3b8; margin-top: -2px;">${d.dateSub}</div>
          <div style="font-size: 1.35rem; margin: 2px 0; line-height: 1;" title="${d.condition}">${d.icon}</div>
          <div style="font-size: 0.85rem; font-weight: 800; color: #ffffff; font-family: monospace;">${d.high}°</div>
          <div style="font-size: 0.72rem; color: #94a3b8; font-family: monospace; margin-top: -3px;">${d.low}°</div>
          <div style="margin-top: 2px;">${rainHtml}</div>
          <div style="font-size: 0.65rem; color: #cbd5e1; display: flex; align-items: center; gap: 2px; font-family: monospace;">
            <svg style="width: 10px; height: 10px; transform: rotate(${d.windDirDeg}deg); color: #00d4ff; vertical-align: middle;" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" />
            </svg>
            <span>${d.windSpeed} ${speedUnit}</span>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="metric-card" style="background: linear-gradient(145deg, #13151f 0%, #0d0e15 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 12px 14px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; flex-wrap: wrap; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
            <span style="font-size: 0.9rem; font-weight: 700; color: #fff;">📊 ${title}</span>
            ${widget.options?.allowSourceSelection !== false ? `
            <div style="display: inline-flex; align-items: center; gap: 4px;">
              <span style="font-size: 0.65rem; color: #94a3b8; font-weight: 600;">SOURCE:</span>
              <select class="daily-source-select" style="background: rgba(0, 212, 255, 0.12); color: #00d4ff; border: 1px solid rgba(0, 212, 255, 0.35); border-radius: 6px; padding: 2px 6px; font-size: 0.7rem; font-weight: 700; cursor: pointer; outline: none;">
                <option value="auto" ${activeSourceKey === 'auto' ? 'selected' : ''}>Auto (${autoAgency})</option>
                <option value="nws" ${activeSourceKey === 'nws' ? 'selected' : ''}>NWS (NOAA)</option>
                <option value="eccc" ${activeSourceKey === 'eccc' ? 'selected' : ''}>ECCC (Canada)</option>
                <option value="ecmwf" ${activeSourceKey === 'ecmwf' ? 'selected' : ''}>ECMWF (IFS)</option>
                <option value="icon13" ${activeSourceKey === 'icon13' ? 'selected' : ''}>ICON-13 (DWD)</option>
                <option value="hrrr" ${activeSourceKey === 'hrrr' ? 'selected' : ''}>HRRR (3km)</option>
                <option value="rrfs" ${activeSourceKey === 'rrfs' ? 'selected' : ''}>RRFS (3km)</option>
                <option value="rdps" ${activeSourceKey === 'rdps' ? 'selected' : ''}>RDPS (10km)</option>
                <option value="gdps" ${activeSourceKey === 'gdps' ? 'selected' : ''}>GDPS (15km)</option>
                <option value="gfs" ${activeSourceKey === 'gfs' ? 'selected' : ''}>GFS (0.25°)</option>
              </select>
            </div>
            ` : `
            <span style="font-size: 0.65rem; padding: 2px 6px; background: rgba(0, 212, 255, 0.12); color: #00d4ff; border-radius: 4px; font-weight: 700; border: 1px solid rgba(0, 212, 255, 0.25); letter-spacing: 0.3px;">${activeSourceKey.toUpperCase()}</span>
            `}
          </div>
          <div class="daily-horizon-pills" style="display: inline-flex; background: rgba(0, 0, 0, 0.45); border-radius: 6px; padding: 2px; border: 1px solid rgba(255, 255, 255, 0.08);">
            <button class="daily-h-btn ${is3 ? 'active' : ''}" data-days="3" style="background: ${is3 ? '#00d4ff' : 'transparent'}; color: ${is3 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.68rem; font-weight: 700; padding: 2px 6px; border-radius: 4px; cursor: pointer;">3D</button>
            <button class="daily-h-btn ${is5 ? 'active' : ''}" data-days="5" style="background: ${is5 ? '#00d4ff' : 'transparent'}; color: ${is5 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.68rem; font-weight: 700; padding: 2px 6px; border-radius: 4px; cursor: pointer;">5D</button>
            <button class="daily-h-btn ${is7 ? 'active' : ''}" data-days="7" style="background: ${is7 ? '#00d4ff' : 'transparent'}; color: ${is7 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.68rem; font-weight: 700; padding: 2px 6px; border-radius: 4px; cursor: pointer;">7D</button>
            <button class="daily-h-btn ${isMax ? 'active' : ''}" data-days="max" style="background: ${isMax ? '#00d4ff' : 'transparent'}; color: ${isMax ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.68rem; font-weight: 700; padding: 2px 6px; border-radius: 4px; cursor: pointer;">Max</button>
          </div>
        </div>
        <div style="display: flex; gap: 8px; overflow-x: auto; padding-bottom: 4px; scrollbar-width: thin;">
          ${tilesHtml || ((ctx.data?.accessDenial || ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('forecast_point_tiles'))
            ? `<div style="width: 100%;">${TierAccessService.buildTrendChartDenialHtml({
                denial: ctx.data?.accessDenial || ForecastBinaryService.lastAccessDenial,
                requiredTier: 'pro',
                featureLabel: `${title} (${activeSourceKey.toUpperCase()})`,
                compact: true
              })}</div>`
            : '<div style="color: #94a3b8; font-size: 0.8rem; text-align: center; padding: 12px; width: 100%;">No daily forecast data available</div>')}
        </div>
        <div id="strip-tray-container-${widget.id}" style="display: none; margin-top: 8px;"></div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const container = document.querySelector(`[data-widget-id="${widget.id}"]`) || document;
    const buttons = container.querySelectorAll('.daily-h-btn');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const d = (btn as HTMLElement).dataset.days;
        if (d && ctx.onHorizonChange) {
          ctx.onHorizonChange(d === 'max' ? 'max' : parseInt(d, 10));
        }
      });
    });

    const sourceSelect = container.querySelector('.daily-source-select') as HTMLSelectElement;
    if (sourceSelect) {
      sourceSelect.addEventListener('change', (e) => {
        e.stopPropagation();
        const val = sourceSelect.value;
        if (ctx.onSourceChange) {
          ctx.onSourceChange(val);
        }
      });
    }

    const enableHourly = widget.options?.enableHourlyExpansion !== false;
    if (enableHourly) {
      const tiles = container.querySelectorAll(`[data-widget-id="${widget.id}"].daily-strip-tile`);
      const trayContainer = container.querySelector(`#strip-tray-container-${widget.id}`) as HTMLElement;
      let activeIdx: number | null = null;

      tiles.forEach(tile => {
        tile.addEventListener('click', (e) => {
          e.stopPropagation();
          const dayIdxStr = (tile as HTMLElement).dataset.dayIdx;
          if (dayIdxStr === undefined || !trayContainer) return;
          const dIdx = parseInt(dayIdxStr, 10);

          if (activeIdx === dIdx) {
            // Collapse
            activeIdx = null;
            trayContainer.style.display = 'none';
            trayContainer.innerHTML = '';
            tiles.forEach(t => {
              (t as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.07)';
              (t as HTMLElement).style.background = 'rgba(15, 23, 42, 0.65)';
            });
            return;
          }

          // Expand selected day
          activeIdx = dIdx;
          tiles.forEach(t => {
            (t as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.07)';
            (t as HTMLElement).style.background = 'rgba(15, 23, 42, 0.65)';
          });
          (tile as HTMLElement).style.borderColor = '#00d4ff';
          (tile as HTMLElement).style.background = 'rgba(0, 212, 255, 0.12)';

          const effectiveHorizon = (widget.options?.period && widget.options.period !== 'auto')
            ? widget.options.period
            : ctx.viewDays;
          const hPoints = aggregate3HourIntervals(ctx, dIdx, effectiveHorizon);
          const days = aggregateDailyForecast(ctx, effectiveHorizon);
          const dayInfo = days[dIdx] || { dayLabel: `Day ${dIdx + 1}`, dateSub: '' };

          const colsHtml = hPoints.map(p => `
            <div style="background: rgba(15, 23, 42, 0.65); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 6px; padding: 6px 2px; display: flex; flex-direction: column; align-items: center; gap: 3px; text-align: center; min-width: 0;">
              <div style="font-size: 0.62rem; color: #94a3b8; font-weight: 600;">${p.timeLabel}</div>
              <div style="font-size: 1.15rem; margin: 1px 0; line-height: 1;" title="${p.condition}">${p.icon}</div>
              <div style="font-size: 0.78rem; font-weight: 800; color: #ffffff; font-family: monospace;">${p.temp}°</div>
              <div style="font-size: 0.62rem; font-weight: 700; color: #00d4ff; font-family: monospace;">${p.precipPct > 0 ? `💧${p.precipPct}%` : '<span style="color:#64748b; opacity:0.35;">--</span>'}</div>
              <div style="font-size: 0.6rem; color: #cbd5e1; display: flex; align-items: center; justify-content: center; gap: 2px; font-family: monospace;">
                <svg style="width: 8px; height: 8px; transform: rotate(${p.windDirDeg}deg); color: #00d4ff;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" /></svg>
                <span>${p.windSpeed}m</span>
              </div>
            </div>
          `).join('');

          trayContainer.innerHTML = `
            <div style="font-size: 0.72rem; font-weight: 700; color: #00d4ff; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">
              <span>3-Hour Glance: ${dayInfo.dayLabel} (${dayInfo.dateSub})</span>
            </div>
            <div style="width: 100%; background: rgba(11, 17, 30, 0.95); border: 1px solid rgba(0, 212, 255, 0.25); border-radius: 8px; padding: 8px 6px; display: grid; grid-template-columns: repeat(${Math.max(1, hPoints.length)}, minmax(0, 1fr)); gap: 4px; box-sizing: border-box;">
              ${colsHtml}
            </div>
          `;
          trayContainer.style.display = 'block';
        });
      });
    }
  }
}
